import { useNavigate } from 'react-router-dom';
import { hasKey, t } from '@app/i18n';
import { Icon, type IconName } from '@zarya/theme';
import { Alert, Card } from '@zarya/ui';
import type { Overview, FirstStep } from './Overview.api';
import { cardFailure, failureText } from './degradation';
import s from './Overview.module.css';

/**
 * Card **Primeiros passos** — "o que falta para o tenant operar" (FR-094).
 *
 * Cada item é um botão que navega para a tela que resolve a pendência; o destino
 * vem do contrato (`link`), não de um mapa aqui — quem sabe onde se resolve cada
 * passo é o servidor que sabe por que ele está pendente.
 *
 * Três estados, não dois: `feito: null` é **indeterminado** (o middleware não
 * respondeu por este item) e não pode aparecer como feito nem como pendente.
 */

type StepState = 'ok' | 'todo' | 'na';

/** Ícone e classe são do desenho; o rótulo vem do catálogo na hora de pintar. */
const STATE: Record<StepState, { icon: IconName; classe: string }> = {
  ok: { icon: 'check', classe: s.stOk },
  todo: { icon: 'arrow-right', classe: s.stTodo },
  na: { icon: 'question', classe: s.stNa },
};

function stepLabel(estado: StepState): string {
  return t(`overview.firstSteps.state.${estado}`);
}

/**
 * O nome do passo, **pela chave do contrato**.
 *
 * O servidor manda `key` (enum) e `label` (frase pronta, em português) lado a
 * lado. Traduzir pelo `label` era impossível — nenhuma tradução do cliente
 * alcança frase que chega pronta, e era a última coisa em português no console
 * em inglês. Pelo `key` o enum é dado e a frase é texto, que é a divisão que
 * vale no resto do produto.
 *
 * O `label` do servidor fica de reserva para uma chave que ele passe a mandar
 * antes de nós a conhecermos: melhor a frase em português do que a chave crua.
 */
function stepName(step: FirstStep): string {
  const chave = `overview.firstSteps.step.${step.key}`;
  return hasKey(chave) ? t(chave) : step.label;
}

function stepState(feito: FirstStep['done']): StepState {
  if (feito === true) return 'ok';
  if (feito === false) return 'todo';
  return 'na';
}

export interface FirstStepsCardProps {
  overview: Overview;
}

export function FirstStepsCard({ overview }: FirstStepsCardProps) {
  const navigate = useNavigate();
  const falha = cardFailure(overview.failures, 'primeiros_passos');
  const passos = overview.first_steps;
  const prontos = passos.filter((step) => step.done === true).length;

  return (
    <Card
      title={t('overview.firstSteps.title')}
      subtitle={
        falha
          ? undefined
          : t('overview.firstSteps.subtitle', { done: prontos, total: passos.length })
      }
      tip={t('overview.firstSteps.tip')}
    >
      {falha ? <Alert tone="warning">{failureText(falha)}</Alert> : null}

      {!falha && passos.length === 0 ? (
        <p className={s.vazio}>{t('overview.firstSteps.empty')}</p>
      ) : null}

      {/* Com a leitura degradada a lista sairia de cache: um estado antigo
          apresentado como atual é pior que a ausência dele. */}
      {!falha && passos.length > 0 ? (
        <ul className={s.passos}>
          {passos.map((step) => {
            const chave = stepState(step.done);
            const estado = STATE[chave];
            return (
              <li key={step.key}>
                <button
                  type="button"
                  data-testid={`passo-${step.key}`}
                  onClick={() => navigate(step.link)}
                >
                  {/* Ícone decorativo: quem nomeia o estado é a linha de baixo —
                      duplicar o rótulo aqui só engrossaria o nome do botão. */}
                  <span className={[s.st, estado.classe].join(' ')}>
                    <Icon name={estado.icon} size="xs" aria-hidden="true" />
                  </span>
                  <span className={s.passoTexto}>
                    <b>{stepName(step)}</b>
                    <span>
                      {stepLabel(chave)}
                      {step.optional ? t('overview.firstSteps.optional') : ''}
                    </span>
                  </span>
                  <Icon name="chevron" size="sm" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </Card>
  );
}
